// watch/actionStyles.ts — the watch page's action row buttons (1.27.50, owner concept
// video_watch_page_v2): rounded pills with an icon and a label. Hover is a slight fill
// change, never a stroke change (owner's anti-"AI slop" rule).
import styled from 'styled-components';

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ActionPill = styled.button<{ $on?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.surface)};
  color: ${({ theme, $on }) => ($on ? theme.color.onAccent : theme.color.textPrimary)};
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    transform 80ms ease;
  &:hover:not(:disabled) {
    background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.surfaceRaised)};
    filter: ${({ $on }) => ($on ? 'brightness(1.08)' : 'none')};
  }
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/** The Like | Dislike pair: one pill split in two. */
export const Segmented = styled.div`
  display: inline-flex;
  align-items: stretch;
  height: 40px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  overflow: hidden;
`;

export const SegmentBtn = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  border: 0;
  background: ${({ theme, $on }) => ($on ? theme.color.hover : 'transparent')};
  color: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.textPrimary)};
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: background-color 120ms ease;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.surfaceRaised};
  }
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
  & + & {
    border-left: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
`;

/** The dropdown panels hung off the row (Save, ···). */
export const MenuPanel = styled.div`
  min-width: 250px;
  padding: 4px 0;
  h3 {
    margin: 6px 12px 4px;
    font: 700 11px ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
  }
  hr {
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
    margin: 4px 0;
  }
`;

export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.hover};
  }
  &:disabled {
    color: ${({ theme }) => theme.color.textMuted};
    cursor: default;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
  &[data-danger='true'] {
    color: ${({ theme }) => theme.color.danger};
  }
  /* The label (after the icon: Icon renders a span too) takes the row; a trailing
     chevron stays at the end. */
  > span:nth-child(2) {
    flex: 1;
  }
`;

/** Flash's action column (owner concept shorts_player): a round button over its label. */
export const RoundAction = styled.button<{ $on?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.color.textPrimary};
  font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  & > span:first-child {
    display: grid;
    place-items: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.surface)};
    color: ${({ theme, $on }) => ($on ? theme.color.onAccent : theme.color.textPrimary)};
    transition:
      background-color 120ms ease,
      transform 120ms ease;
  }
  &:hover:not(:disabled) > span:first-child {
    background: ${({ theme, $on }) => ($on ? theme.color.accentCyan : theme.color.surfaceRaised)};
    transform: scale(1.05);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  &:focus-visible {
    outline: none;
  }
  &:focus-visible > span:first-child {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;
